/**
 * ScreeningQuestions Component
 * Displays screening questions with audio recording functionality
 */

import React, { useState } from 'react';
import { 
  Mic, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Award,
  Loader,
  Volume2
} from 'lucide-react';
import AudioRecorder from './AudioRecorder';
import useAudioRecording from '../hooks/useAudioRecording';
import { toast } from 'react-hot-toast';

/**
 * ScreeningQuestions Component
 * 
 * @param {function} onComplete - Callback when screening is completed
 * @returns {JSX.Element} ScreeningQuestions component
 */
const ScreeningQuestions = ({ onComplete }) => {
  const {
    screeningQuestions,
    screeningProfile,
    loading,
    completeScreening,
    hasResponseForQuestion,
    getProgress,
    isScreeningComplete,
    getStats
  } = useAudioRecording();

  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completing, setCompleting] = useState(false);

  // Handle recording completion
  const handleRecordingComplete = (audioData) => {
    toast.success('Recording saved successfully!');
    
    // Auto-advance to next question if not the last one
    if (currentQuestionIndex < screeningQuestions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 1000);
    }
  };

  // Handle complete screening
  const handleCompleteScreening = async () => {
    if (!isScreeningComplete()) {
      toast.error('Please answer all questions before completing');
      return;
    }

    try {
      setCompleting(true);
      await completeScreening();
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error completing screening:', error);
    } finally {
      setCompleting(false);
    }
  };

  // Navigation functions
  const goToNext = () => {
    if (currentQuestionIndex < screeningQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Get current question and stats
  const currentQuestion = screeningQuestions[currentQuestionIndex];
  const stats = getStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading screening questions...</p>
        </div>
      </div>
    );
  }

  if (screeningQuestions.length === 0) {
    return (
      <div className="text-center py-12">
        <Volume2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Screening Questions Available</h3>
        <p className="text-gray-600">
          Screening questions haven't been set up yet. Check back later!
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header with Progress */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audio Screening</h1>
            <p className="text-gray-600 mt-1">
              Answer these questions to strengthen your job applications
            </p>
          </div>
          
          {screeningProfile ? (
            <div className="flex items-center bg-green-50 px-4 py-2 rounded-lg">
              <Award className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-800 font-medium">Profile Complete</span>
            </div>
          ) : (
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{getProgress()}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{stats.totalResponses} of {stats.totalQuestions} answered</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${getProgress()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Question Navigation */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center space-x-2 overflow-x-auto">
          {screeningQuestions.map((question, index) => (
            <button
              key={question.id}
              onClick={() => goToQuestion(index)}
              className={`flex-shrink-0 w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                index === currentQuestionIndex
                  ? 'bg-blue-500 text-white'
                  : hasResponseForQuestion(question.id)
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {hasResponseForQuestion(question.id) ? (
                <CheckCircle className="w-5 h-5 mx-auto" />
              ) : (
                index + 1
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Current Question */}
      {currentQuestion && (
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          {/* Question Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold">{currentQuestionIndex + 1}</span>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Question {currentQuestionIndex + 1} of {screeningQuestions.length}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {currentQuestion.category || 'General'}
                  </p>
                </div>
              </div>

              {hasResponseForQuestion(currentQuestion.id) ? (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-medium">Answered</span>
                </div>
              ) : (
                <div className="flex items-center text-gray-500">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>Not answered</span>
                </div>
              )}
            </div>

            {/* Question Text */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-lg text-gray-800 leading-relaxed">
                {currentQuestion.question}
              </p>
              {currentQuestion.description && (
                <p className="text-sm text-gray-600 mt-2">
                  {currentQuestion.description}
                </p>
              )}
            </div>
          </div>

          {/* Audio Recorder */}
          <div className="p-6">
            <AudioRecorder
              questionId={currentQuestion.id}
              questionText={currentQuestion.question}
              onRecordingComplete={handleRecordingComplete}
              maxDuration={currentQuestion.maxDuration || 120}
            />
          </div>
        </div>
      )}

      {/* Navigation Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={goToPrevious}
          disabled={currentQuestionIndex === 0}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            currentQuestionIndex === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </button>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {screeningQuestions.length}
          </p>
        </div>

        {currentQuestionIndex === screeningQuestions.length - 1 ? (
          <button
            onClick={handleCompleteScreening}
            disabled={!isScreeningComplete() || completing}
            className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
              !isScreeningComplete() || completing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {completing ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <Award className="w-4 h-4 mr-2" />
                Complete Screening
              </>
            )}
          </button>
        ) : (
          <button
            onClick={goToNext}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>

      {/* Completion Message */}
      {isScreeningComplete() && !screeningProfile && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
            <div>
              <h3 className="font-medium text-green-800">All questions answered!</h3>
              <p className="text-sm text-green-700 mt-1">
                Click "Complete Screening" to finalize your profile and make it available to employers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Screening Tips:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Speak clearly and confidently</li>
          <li>• Keep responses between 30-90 seconds</li>
          <li>• Use specific examples when possible</li>
          <li>• Practice your answers before recording</li>
          <li>• Record in a quiet environment</li>
        </ul>
      </div>
    </div>
  );
};

export default ScreeningQuestions;